import { useState } from "react";

import { type FindingSeverity } from "@/lib/api/building";
import { type KBZoneSummary, type ZoneKpis } from "@/lib/api/agent";
import { cn } from "@/lib/utils";
import { zoneSymbol, type Zone } from "@/lib/building-domain";
import {
  inputGlowWrap,
  inputGlowInner,
  cardDestructive,
  moduleCard,
} from "@/lib/card-styles";
import { CardGlowOverlay } from "@/components/ui/card-glow-overlay";
import { AccentRailDisclosure } from "@/components/ui/accent-rail-disclosure";
import { ZoneKpiMeter } from "@/components/ui/zone-kpi-meter";
import { ZoneKpiSourceBadge } from "@/components/ui/zone-kpi-badge";
import {
  ZONE_KPI_LABEL,
  zoneKpiOfOrUnavailable,
  zoneKpiSourceChip,
  type ZoneKpiKind,
} from "@/lib/zone-kpi";
import {
  discard_zone_draft,
  dirty_actions_visible,
  is_zone_draft_dirty,
  zone_draft_from_card,
  zone_type_collapsed_value,
  type ZoneStructureDraft,
} from "./zone-row-chrome";

// ── floor_label (local copy — also used in ZoneGrid) ─────────────────────────

function floor_label(floor: number): string {
  if (floor === 0) return "Rakennustaso";
  return `Kerros ${floor}`;
}

const ZONE_KPI_KINDS: ZoneKpiKind[] = [
  "occupancy",
  "air_quality",
  "temperature",
];

interface ZoneKpiTileRow {
  kind: ZoneKpiKind;
  label: string;
  kpi: ReturnType<typeof zoneKpiOfOrUnavailable>;
  chip: ReturnType<typeof zoneKpiSourceChip>;
}

// Always all three KPI graphics (@/lib/zone-kpi.ts). A not-ready KPI still occupies its
// slot as an empty meter + "ei saatavilla" — never a fabricated 0 % (that would read as mid).
function zoneKpiTileRows(kpis: ZoneKpis | null): ZoneKpiTileRow[] {
  return ZONE_KPI_KINDS.map((kind) => {
    const kpi = zoneKpiOfOrUnavailable(kpis, kind);
    return {
      kind,
      label: ZONE_KPI_LABEL[kind],
      kpi,
      chip: zoneKpiSourceChip(kpi),
    };
  });
}

// ── TileProps ─────────────────────────────────────────────────────────────────

export interface TileProps {
  zone: Zone;
  kb_zone_types: KBZoneSummary[];
  expanded: boolean;
  on_toggle: () => void;
  operator: boolean;
  max_severity?: FindingSeverity;
  // The zone's resolved KPI triple — käyttöaste, sisäilman laatu, lämpötila.
  // A not-ready KPI still renders its row as "ei saatavilla".
  kpis: ZoneKpis | null;
  kpis_pending?: boolean;
  // Equipment that lists this zone under Palvelee vyöhykkeitä — inverse of the
  // equipment-row chips. Read-only here; links are edited on the equipment card.
  serving_equipment?: { id: string; name: string }[];
  on_update: (
    zone_id: string,
    data: {
      name?: string;
      floor?: number;
      zone_type?: string | null;
      icon?: string;
      description?: string;
    },
  ) => Promise<void>;
  on_delete: (id: string) => Promise<void>;
}

// ── ZoneTile ──────────────────────────────────────────────────────────────────

export function ZoneTile({
  zone,
  kb_zone_types,
  expanded,
  on_toggle,
  operator,
  max_severity,
  kpis,
  kpis_pending = false,
  serving_equipment = [],
  on_update,
  on_delete,
}: TileProps) {
  const [confirm_delete, set_confirm_delete] = useState(false);
  const [name, set_name] = useState(zone.name);
  const [floor, set_floor] = useState(zone.floor);
  const [zone_type, set_zone_type] = useState<string>(zone.zone_type ?? "");
  const [description, set_description] = useState(zone.description);
  const [submitting, set_submitting] = useState(false);
  const kpi_rows = zoneKpiTileRows(kpis);

  // KB is the source of truth for zone symbols (getKBTypes().icon); fall back to the
  // static mirror only until the KB summary loads.
  const zone_icon = (type: string | null | undefined): string =>
    kb_zone_types.find((zt) => zt.id === type)?.icon ?? zoneSymbol(type);

  const saved_draft = zone_draft_from_card(zone);
  const current_draft: ZoneStructureDraft = {
    name,
    floor,
    zone_type,
    description,
  };
  const dirty = is_zone_draft_dirty(current_draft, saved_draft);
  const show_dirty_actions = dirty_actions_visible(dirty);

  function apply_draft(next: ZoneStructureDraft) {
    set_name(next.name);
    set_floor(next.floor);
    set_zone_type(next.zone_type);
    set_description(next.description);
  }

  async function save_draft() {
    set_submitting(true);
    try {
      await on_update(zone.id, {
        name: name.trim(),
        floor,
        zone_type: zone_type || null,
        description: description.trim(),
      });
      apply_draft({
        name: name.trim(),
        floor,
        zone_type,
        description: description.trim(),
      });
    } finally {
      set_submitting(false);
    }
  }

  const type_name = zone_type_collapsed_value(kb_zone_types, zone_type);

  if (confirm_delete) {
    return (
      <div className={cn(cardDestructive, "flex flex-col gap-2 p-4 text-xs")}>
        <p className="font-medium">Poistetaanko &ldquo;{zone.name}&rdquo;?</p>
        <div className="flex gap-2">
          <button
            onClick={() => void on_delete(zone.id)}
            className="rounded bg-destructive px-2 py-1 text-destructive-foreground hover:bg-destructive/80"
          >
            Poista
          </button>
          <button
            onClick={() => set_confirm_delete(false)}
            className="rounded px-2 py-1 text-muted-foreground hover:text-foreground"
          >
            Peruuta
          </button>
        </div>
      </div>
    );
  }

  if (operator) {
    const zone_accent =
      "color-mix(in oklab, var(--muted-foreground) 45%, transparent)";
    return (
      <div className="group relative">
        <AccentRailDisclosure
          open={expanded}
          onToggle={on_toggle}
          kind="group"
          accentColor={zone_accent}
          triggerClassName="items-start gap-2.5 px-3 py-2.5 hover:bg-surface/60"
          chevronClassName="h-4 w-4"
          actions={
            show_dirty_actions ? (
              <div className="flex shrink-0 items-center gap-1.5 py-2 pr-8">
                <button
                  type="button"
                  disabled={submitting || !name.trim()}
                  onClick={() => void save_draft()}
                  className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
                >
                  {submitting ? "Tallennetaan..." : "Tallenna"}
                </button>
                <button
                  type="button"
                  onClick={() => apply_draft(discard_zone_draft(saved_draft))}
                  className="rounded px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                >
                  Peruuta
                </button>
              </div>
            ) : undefined
          }
          trigger={
            <>
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg leading-none"
                style={{
                  backgroundColor: `color-mix(in oklab, ${zone_accent} 18%, transparent)`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklab, ${zone_accent} 45%, transparent)`,
                }}
                aria-hidden
              >
                {zone_icon(zone_type || zone.zone_type)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{name}</div>
                <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                  <span className="font-medium">Tyyppi</span>
                  {" · "}
                  {type_name}
                </div>
                <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
                  <span className="font-medium">Palveleva talotekniikka</span>
                  {serving_equipment.length === 0 ? (
                    <span>· —</span>
                  ) : (
                    serving_equipment.map((eq) => (
                      <span
                        key={eq.id}
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
                      >
                        {eq.name}
                      </span>
                    ))
                  )}
                </div>
              </div>
              {max_severity && (
                <span
                  className={cn(
                    "mt-1 h-2 w-2 shrink-0 rounded-full",
                    max_severity === "critical"
                      ? "bg-destructive"
                      : max_severity === "warning"
                        ? "bg-warning"
                        : "bg-primary/40",
                  )}
                  title={
                    max_severity === "critical"
                      ? "Kriittisiä havaintoja"
                      : max_severity === "warning"
                        ? "Varoituksia"
                        : "Huomioita"
                  }
                />
              )}
            </>
          }
        >
          {expanded && (
            <div className="space-y-2 border-t border-border/40 px-3 py-2.5 text-sm">
              <div className={inputGlowWrap}>
                <div className={inputGlowInner}>
                  <input
                    autoFocus
                    value={name}
                    onChange={(e) => set_name(e.target.value)}
                    placeholder="Vyöhykkeen nimi"
                    className="w-full bg-transparent px-2 py-1.5 text-sm placeholder:text-muted-foreground outline-none"
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  Kerros
                  <div className={inputGlowWrap}>
                    <div className={inputGlowInner}>
                      <input
                        type="number"
                        value={floor}
                        onChange={(e) => set_floor(Number(e.target.value))}
                        className="w-14 bg-transparent px-2 py-1 text-xs outline-none"
                      />
                    </div>
                  </div>
                </label>
                <label className="flex flex-1 items-center gap-1.5 text-xs text-muted-foreground">
                  Tyyppi
                  <select
                    value={zone_type}
                    onChange={(e) => set_zone_type(e.target.value)}
                    className="flex-1 rounded border border-border/40 bg-background px-2 py-1 text-xs outline-none focus:border-primary/60"
                  >
                    <option value="">— Ei tyyppiä —</option>
                    {kb_zone_types.map((zt) => (
                      <option key={zt.id} value={zt.id}>
                        {zt.icon} {zt.name_fi}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className={inputGlowWrap}>
                <div className={inputGlowInner}>
                  <input
                    value={description}
                    onChange={(e) => set_description(e.target.value)}
                    placeholder="Kuvaus (valinnainen)"
                    className="w-full bg-transparent px-2 py-1.5 text-xs placeholder:text-muted-foreground outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">
                  Palveleva talotekniikka
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {serving_equipment.length === 0 ? (
                    <span className="text-xs text-muted-foreground">
                      Ei palvelevaa talotekniikkaa.
                    </span>
                  ) : (
                    serving_equipment.map((eq) => (
                      <span
                        key={eq.id}
                        className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] text-primary"
                      >
                        {eq.name}
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </AccentRailDisclosure>
        <div className="absolute right-1 top-1 z-10 hidden group-hover:block">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              set_confirm_delete(true);
            }}
            className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
            title="Poista"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  // `contents` so the button is the grid item. Three in-flow children +
  // `grid-rows-subgrid row-span-3` share header / readings / Palveleva
  // talotekniikka tracks with sibling cards in the row.
  return (
    <div className="contents">
      <button
        onClick={on_toggle}
        className={cn(
          moduleCard,
          "grid w-full cursor-pointer grid-rows-subgrid row-span-3 gap-2 p-4 text-left",
          expanded ? "border-primary/50 ring-1 ring-primary/30" : "",
        )}
      >
        <div>
          <div className="pointer-events-none absolute inset-0">
            <CardGlowOverlay />
          </div>
          <div className="relative flex items-start gap-2">
            <span className="mt-0.5 text-xl">{zone_icon(zone.zone_type)}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">
                {zone.name}
              </p>
              <p className="font-mono text-xs text-muted-foreground">
                {floor_label(zone.floor)}
              </p>
            </div>
            {max_severity && (
              <span
                className={cn(
                  "mt-1 h-2 w-2 shrink-0 rounded-full",
                  max_severity === "critical"
                    ? "bg-destructive"
                    : max_severity === "warning"
                      ? "bg-warning"
                      : "bg-primary/40",
                )}
                title={
                  max_severity === "critical"
                    ? "Kriittisiä havaintoja"
                    : max_severity === "warning"
                      ? "Varoituksia"
                      : "Huomioita"
                }
              />
            )}
          </div>
        </div>

        <div className="relative flex flex-col gap-0.5">
          {kpis_pending ? (
            <p className="text-xs text-muted-foreground">Ladataan…</p>
          ) : (
            kpi_rows.map((row) => (
              <div key={row.kind} className="flex items-end gap-1.5">
                <div className="min-w-0 flex-1">
                  <ZoneKpiMeter
                    kind={row.kind}
                    kpi={row.kpi}
                    label={row.label}
                  />
                </div>
                {row.chip && <ZoneKpiSourceBadge chip={row.chip} />}
              </div>
            ))
          )}
        </div>

        <div className="relative flex flex-wrap items-start gap-1.5 border-t border-border/30 pt-1.5 text-[11px] text-muted-foreground">
          <span className="font-medium">Palveleva talotekniikka</span>
          {serving_equipment.length === 0 ? (
            <span>· —</span>
          ) : (
            serving_equipment.map((eq) => (
              <span
                key={eq.id}
                className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
              >
                {eq.name}
              </span>
            ))
          )}
        </div>
      </button>
    </div>
  );
}
